import {useState, useEffect, useRef, useMemo} from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { NotificationManager } from 'react-notifications';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { showLoader, hideLoader } from '../../../store/common/slice';
import BllinkUtils from '../../../common/utils/utils';
import Button from '../../../common/Button';
import DragAndDrop from '../../../common/simple/dragAndDrop';
import Search from '../../../images/search.svg';
import Question from '../../../images/newDesign/question.svg';
import Upload from '../../../images/newDesign/dragAndDropUpload.svg';
import File from '../../../images/newDesign/file.svg';
import { useAccessToken } from '../../../common/hooks/useAccessToken';
import CacheData from '../../../common/utils/cacheData';
import { StyledReport } from '../../../managers/reports/report.styled';
import ActionsCardWithCheckbox from './actionsCards/ActionsCardWithCheckbox';
import BankActionsCard from './actionsCards/BankActionsCard';
import AutoCompareModal from './modals/AutoCompareModal';
import ManualApproveModal from './modals/ManualApproveModal';
import { PendingActionsWrapper, ActionsWrapper, PendingFooterWrapper } from './BankAdjustmentsNew.styled';
import {fileToBase64} from '../../../../utils/fileToBase64';
import {constants} from '../../../common/constants/constants';
import { ModalManager } from '../../../ui/ModalManager';
import UploadExcelPopUp from './UploadExcelPopUp';

const PendingActions = ({ transactions, buildingID, year, month }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const client = useAccessToken();
    const ltr = useSelector(state => state.common.ltrEnabled);
    const bankRecords = CacheData.fetchCache('bankRecords');

    const inputFile = useRef(null);
    const [bllinkFilter, setBllinkFilter] = useState('');
    const [bankFilter, setBankFilter] = useState('');
    const [bankTransactions, setBankTransactions] = useState(bankRecords);
    const [previouslyUploadedFiles, setPreviouslyUploadedFiles] = useState([]);
    const [checkedBllinkActions, setCheckedBllinkActions] = useState({});
    const [checkedBankActions, setCheckedBankActions] = useState({});
    const [autoBtnDisabled, setAutoBtnDisabled] = useState(true);
    const [manualBtnDisabled, setManualBtnDisabled] = useState(true);
    const [checkedOnlinePayments, setCheckedOnlinePayments] = useState(false);
    const [showAutoCompareModal, setShowAutoCompareModal] = useState(false);
    const [showManualApproveModal, setShowManualApproveModal] = useState(false);
    const [showExcelUploadPopUp, setShowExcelUploadPopUp] = useState(false);
    const [currentUuid, setCurrentUuid] = useState('');
    const [autoMatches, setAutoMatches] = useState([]);
    const [currentBllinkId, setCurrentBllinkId] = useState([]);

    const onlineTransactionIds = useMemo(() =>
        transactions.filter(tr => constants.managerPaymentsOptions.onlineServerPayments.includes(tr.Method || tr.method)).map(tr => tr.paymentID),
        [transactions]
    );

    CacheData.cache('bankAdjustmentsTab', 'pending');

    useEffect(async () => {
        if (client != null) {
            dispatch(showLoader());
            const { reportFiles } = await client.managers.bankAdjustments.getBuildingReportFiles(buildingID);
            setPreviouslyUploadedFiles(reportFiles);
            dispatch(hideLoader());
        }
    }, [client, buildingID]);

    const onBllinkCheckboxChange = (paymentID, state) => {
        setCheckedBllinkActions({
            ...checkedBllinkActions,
            [paymentID]: state
        })
    };

    const onBankCheckboxChange = (confirmationId, state) => {
        setCheckedBankActions({
            ...checkedBankActions,
            [confirmationId]: state
        })
    };

    useEffect(() => {
        let paymentIDs = {};
        for (const transaction of transactions) {
            paymentIDs[transaction.paymentID] = false
        }
        setCheckedBllinkActions(paymentIDs);
    }, [transactions]);

    useEffect(() => {
        let confirmationIDs = {};
        bankTransactions && bankTransactions.forEach(transaction => {
            confirmationIDs[transaction.confirmationId] = false
        });
        setCheckedBankActions(confirmationIDs);
    }, [bankTransactions]);

    const renderTransactions = (transaction, i) => {
        const date = transaction.chargeDate || transaction.PaymentDate;
        const amount = transaction.amount || transaction.Amount;
        const currency = transaction.currency || transaction.payment?.Currency;
        const paymentObject = transaction.expenseTypeKey || transaction.paymentDetails?.PaymentPlan?.ExtraInfo?.ApartmentNumber;
        const paymentMethod = transaction.methodText || transaction.Method || transaction.method;
        const paymentID = transaction.paymentID || transaction.expenseID;
        const paymentType = transaction.type ? transaction.type : (transaction.isIncome ? t('expenses.income') : t('expenses.expense'));
        const method = transaction.Method || transaction.method;
        const showItem = BllinkUtils.Filter.filterItems([transaction.methodText, transaction.type, amount, date, paymentObject, paymentID, paymentMethod], bllinkFilter);

        return (
            showItem && (<ActionsCardWithCheckbox
                key={i}
                date={date}
                amount={amount}
                currency={currency}
                paymentType={paymentType}
                paymentObject={paymentObject}
                paymentMethod={paymentMethod}
                documentNumber={paymentID}
                paymentID={paymentID}
                method={method}
                checked={checkedBllinkActions[paymentID]}
                onChange={onBllinkCheckboxChange}
            />)
        )
    };

    const renderBankTransactions = (transaction, i) => {
        const { date, description, confirmationId, paymentMethod } = transaction;
        const parsedType = transaction.credit ? t('expenses.income') : (transaction.debit ? t('expenses.expense') : '');
        const creditOrDebitNum = transaction.credit ? transaction.credit : (transaction.debit ? transaction.debit : '');

        const showItem = BllinkUtils.Filter.filterItems([date, confirmationId, description, creditOrDebitNum, parsedType], bankFilter);

        return (
            showItem && (<BankActionsCard
                key={i}
                date={date}
                description={description}
                type={parsedType}
                creditOrDebitNum={creditOrDebitNum}
                confirmationId={confirmationId}
                paymentMethod={paymentMethod}
                checked={checkedBankActions[i]}
                onChange={onBankCheckboxChange}
            />)
        )
    };

    const handleFileAdd = async (e, source) => {
        e.preventDefault();
        if (source?.length > 0) {
            const fileCode = await fileToBase64(source[0]);
            const fileName = source[0].name;
            dispatch(showLoader());
            try {
                const { uuid } = await client.managers.bankAdjustments.addReportFile(buildingID, fileName, fileCode);
                setCurrentUuid(uuid);
                const response = await client.managers.bankAdjustments.getReportFileRecords(buildingID, uuid);
                if (!response || response?.error || response?.records.length === 0) setShowExcelUploadPopUp(true)

                else {
                    setBankTransactions(response?.records);
                    CacheData.cache('bankRecords', response?.records);
                    // setAutoBtnDisabled(false);
                }
            } catch (e) {
                setShowExcelUploadPopUp(true)
            }

            dispatch(hideLoader());
        }
    };

    const usePreviouslyUploadedFile = async (uuid) => {
        dispatch(showLoader());
        try {
            setCurrentUuid(uuid);
            const response = await client.managers.bankAdjustments.getReportFileRecords(buildingID, uuid);
            if (!response || response.records.length === 0) NotificationManager.success(t('reports.empty_table'))
            else {
                setBankTransactions(response?.records);
                CacheData.cache('bankRecords', response?.records);
                // setAutoBtnDisabled(false);
            }
        } catch (e) {
            setShowExcelUploadPopUp(true)
        }
        dispatch(hideLoader());
    };

    const autoReconciliation = async () => {
        dispatch(showLoader());
        const { recordMatches, transactions, bankRecords } = await client.managers.bankAdjustments.getMatchedReportFileRecords(buildingID, year, month, currentUuid);
        const matches = recordMatches.map(match => {
            const bllinkRecord = match.expenseID == null ?
                transactions.filter(trx => trx.isIncome && (Array.isArray(trx.paymentID) ? trx.paymentID.some(pid => pid === match.paymentID) : trx.paymentID === match.paymentID)) :
                transactions.filter(trx => !trx.isIncome && trx.expenseID === match.expenseID);
            const bankRecord = bankRecords[match.recordIdx];
            return { bllinkRecord, bankRecord };
        });
        setAutoMatches(matches);
        dispatch(hideLoader());
        setShowAutoCompareModal(true);
    };

    useEffect(() => {
        const checked = Object.entries(checkedBllinkActions).filter(([k, v]) => !!v).map(([k]) => k);
        const onlineChecked = checked.some(id => onlineTransactionIds.includes(+id));
        const bllinkChecked = !!checked.length;
        setManualBtnDisabled(!bllinkChecked || onlineChecked);
        setCheckedOnlinePayments(bllinkChecked && onlineChecked);
    }, [checkedBllinkActions]);

    const manualApprove = () => {
        let bllinkAmount = [];
        let bankAmount = [];
        const bllinkIds = [];
        for (const action of Object.keys(checkedBllinkActions)) {
            if (checkedBllinkActions[action] === true) {
                bllinkIds.push(action);
                const t = transactions.find(t => t.paymentID == action);
                bllinkAmount.push(t.amount || t.Amount);
            }
        }
        setCurrentBllinkId(bllinkIds);
        if (Object.keys(checkedBankActions).length == 0) {
            setShowManualApproveModal(true)
        } else {
            for (const action of Object.keys(checkedBankActions)) {
                if (checkedBankActions[action] === true) {
                    const t = bankTransactions.find(t => t.confirmationId == action);
                    bankAmount.push(Math.abs(t.credit || t.debit));
                }
            }
            const bllinkSum = bllinkAmount.reduce(
                (previousValue, currentValue) => previousValue + currentValue,
                0
            );
            const bankSum = bankAmount.reduce(
                (previousValue, currentValue) => previousValue + currentValue,
                0
            );

            if (bllinkSum === bankSum) setShowManualApproveModal(true);
            else NotificationManager.error(t('expenses.bank_adjustments.manual_error'))
        }
    };

    const onAutoCompareModalHide = () => setShowAutoCompareModal(false);

    const onManualApproveModalHide = () => setShowManualApproveModal(false);

    const onExcelUploadPopUp = () => setShowExcelUploadPopUp(false);

    const clearBankRecords = () => {
        if (!bankTransactions || bankTransactions?.length === 0) return;
        else {
            CacheData.delete('bankRecords');
            window.location.reload();
        }
    };

    return (
        <>
            <PendingActionsWrapper>
                <ActionsWrapper pending ltr={ltr}>
                    <div className='actions-pending-wrapper'>
                        <div className='pending-cards-wrapper'>
                            <StyledReport className='report' ltr={ltr}>
                                <div className='report-wrap'>
                                    <div className='report-top'>
                                        <div className='report-top-start'>
                                            <div className="search">
                                                <input
                                                    type="text"
                                                    name="search"
                                                    onChange={e => setBllinkFilter(e.target.value.toLowerCase())}
                                                />
                                                <img src={Search} alt={t('search.search')} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </StyledReport>
                            <div>
                                {t('expenses.bank_adjustments.bllink_records')}
                                <OverlayTrigger
                                    placement="top"
                                    overlay={props => (
                                        <Tooltip {...props}>
                                            {t('expenses.bank_adjustments.bllink_tooltip')}
                                        </Tooltip>
                                    )}
                                >
                                    <img src={Question} alt='' className='question'/>
                                </OverlayTrigger>
                            </div>
                        </div>
                        {transactions.length === 0 && <div className='no_records'>{t('expenses.bank_adjustments.no_records')}</div>}
                        {transactions.map((transaction, i) => renderTransactions(transaction, i))}
                    </div>
                </ActionsWrapper>

                <ActionsWrapper pending ltr={ltr}>
                    <div className='actions-pending-wrapper'>
                        <div className='pending-cards-wrapper'>
                            <StyledReport className='report' ltr={ltr}>
                                <div className='report-wrap'>
                                    <div className='report-top'>
                                        <div className='report-top-start'>
                                            <div className="search">
                                                <input
                                                    type="text"
                                                    name="search"
                                                    onChange={e => setBankFilter(e.target.value.toLowerCase())}
                                                />
                                                <img src={Search} alt={t('search.search')} className={ltr && 'ltr'} />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </StyledReport>
                            <OverlayTrigger
                                placement="top"
                                overlay={props => (
                                    <Tooltip {...props}>
                                        {t('expenses.bank_adjustments.clear_records')}
                                    </Tooltip>
                                )}
                            >
                                <div className='remove-records' onClick={clearBankRecords}>&#215;</div>
                            </OverlayTrigger>
                            <div>
                                {t('expenses.bank_adjustments.bank_records')}
                                <OverlayTrigger
                                    placement="top"
                                    overlay={props => (
                                        <Tooltip {...props}>
                                            {t('expenses.bank_adjustments.bank_tooltip')}
                                        </Tooltip>
                                    )}
                                >
                                    <img src={Question} alt='' className='question'/>
                                </OverlayTrigger>
                            </div>
                        </div>

                        {(!bankTransactions || bankTransactions?.length === 0) && (
                            <>
                                <input
                                    type='file'
                                    ref={inputFile}
                                    accept=".csv, .xls, .xlsx"
                                    style={{display: 'none'}}
                                    onChange={e => handleFileAdd(e, e.target.files)}
                                />
                                <DragAndDrop handleDrop={e => handleFileAdd(e, e.dataTransfer.files)}>
                                    <div className='drag-and-drop-inner-wrapper' onClick={() => inputFile.current.click()}>
                                        <div>
                                            <img src={Upload} alt='' />
                                        </div>
                                        <div className='drag-and-drop-header'>{t('expenses.bank_adjustments.drag_and_drop')}</div>
                                    </div>
                                </DragAndDrop>
                                <div className='previously-uploaded-files-wrapper'>
                                    {previouslyUploadedFiles.length > 0 && (previouslyUploadedFiles.map((file, i) => (
                                    <div className='previously-uploaded-file' key={i} onClick={() => usePreviouslyUploadedFile(file.uuid)}>
                                        <img src={File} alt="" />
                                        <div>
                                            <span>{file.name}</span>
                                            <span className='size-span'>{file.size} {t('expenses.bank_adjustments.bytes')}</span>
                                        </div>
                                    </div>
                                )))}
                                {previouslyUploadedFiles.length === 0 && <div className='no-files'>{t('expenses.bank_adjustments.no_files')}</div>}
                            </div>
                        </>
                    )}
                    {bankTransactions && bankTransactions?.length === 0 && <div className='no_records'>{t('expenses.bank_adjustments.no_records')}</div>}
                    {bankTransactions && bankTransactions?.length > 0 && bankTransactions.map((transaction, i) => renderBankTransactions(transaction, i))}</div>
                </ActionsWrapper>
            </PendingActionsWrapper>

            <PendingFooterWrapper>
                <div className='footer-header'>{t('expenses.bank_adjustments.reconciliation_header')}</div>
                <div className='buttons-wrapper'>
                    <Button
                        onClick={autoReconciliation}
                        disabled={autoBtnDisabled}
                    >
                        {t('expenses.bank_adjustments.start_auto_reconciliation')}
                    </Button>
                    <OverlayTrigger
                        placement="top"
                        overlay={props => (
                            (manualBtnDisabled && checkedOnlinePayments) ?
                            <Tooltip {...props}>
                                {t('expenses.bank_adjustments.offline_only')}
                            </Tooltip> : <div />
                        )}
                    >
                        <div>
                            <Button
                                outlined
                                onClick={manualApprove}
                                disabled={manualBtnDisabled}
                            >
                                {t('expenses.bank_adjustments.manual_reconciliation')}
                            </Button>
                        </div>
                    </OverlayTrigger>
                </div>
            </PendingFooterWrapper>

            <AutoCompareModal
                show={showAutoCompareModal}
                onModalHide={onAutoCompareModalHide}
                matches={autoMatches}
            />

            <ManualApproveModal
                show={showManualApproveModal}
                onModalHide={onManualApproveModalHide}
                bllinkId={currentBllinkId}
            />

            <UploadExcelPopUp 
                show={showExcelUploadPopUp}
                onModalHide={onExcelUploadPopUp}
            />
        </>
    );
};

export default PendingActions;
